// AppShell — the redesign's chrome around every signed-in /profile/* page.
//
// Desktop (> 720 px): the mockups' 60 px header — "Cryptologics" wordmark,
// Portfolio · AutoTrader · Scanners · Watchlist · Data · Research, and
// "Settings" on the right. Settings is now the trigger of the user menu (gap
// element, DECISIONS A1): Settings, Billing, About, Terms, Privacy, Sign out.
// A "Trading halted" chip appears left of it only while a halt is active
// (not drawn in any mockup; audit A §1.3 — a halted user must see it on every
// page).
//
// Phone (≤ 720 px): phone_lib.js — 56 px header (page title, optional back and
// action), fixed 64 px bottom tab bar Portfolio / AutoTrader / Scanners /
// Research / More, and the More sheet (gap element): Watchlist, Data,
// Settings, Billing, About, Terms, Privacy, Sign out.
//
// Only ONE of the two chromes is mounted at a time, so each `data-tour` anchor
// (nav-portfolio, nav-autotrader, …) exists exactly once for Tour.jsx.
//
// Everything renders inside `.ui-root`; un-migrated pages are wrapped in
// <LegacyPage> by App.js, which opts them out of the reset.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { ShellContext } from './ShellContext';
import { MAIN_NAV, TAB_BAR, ACCOUNT_MENU, MORE_SHEET, activeSection, activeTab, isSettingsPath, defaultPhoneTitle } from './navConfig';
import useRiskWatch from './useRiskWatch';
import RunningIndicator from './RunningIndicator';
import { useBotRoomSync, useBotFeedSync } from '../data/liveCache';
import { logoutFlow } from '../../containers/Login/Logout';
import { Icon, Chevron, BackArrow } from '../components/icons';
import Popover, { Menu, MenuItem, MenuSeparator } from '../components/Popover';
import Layer from '../components/Layer';
import { ToastViewport } from '../components/Toast';
import { cx, useIsPhone } from '../components/util';

// Routes where RiskControls is mounted inside this shell and already toasts
// `risk:blocked`. None today: the redesigned /profile/portfolio does not toast
// it, and /profile/legacy/portfolio renders outside the shell.
const RISK_PANEL_ROUTES = [];

const starts = (path, prefix) => path === prefix || path.startsWith(`${prefix}/`);

function useSignOut() {
  const history = useHistory();
  const [busy, setBusy] = useState(false);
  const run = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    await logoutFlow(history);
  }, [busy, history]);
  return [run, busy];
}

export function HaltChip({ risk, onClick }) {
  if (!risk || !risk.halted) return null;
  const label = risk.global ? 'Trading halted: platform wide' : 'Trading halted';
  return (
    <Link
      to="/profile/portfolio"
      className={cx('ui-haltchip', risk.global && 'ui-haltchip--global')}
      title={risk.reason ? `Reason: ${risk.reason}` : 'New trades are blocked. Open Portfolio to resume.'}
      onClick={onClick}
    >
      <span className="ui-dot ui-dot--warn" style={{ background: 'currentColor' }} aria-hidden="true" />
      {label}
    </Link>
  );
}

function UserMenu({ pathname }) {
  const btn = useRef(null);
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const [signOut, busy] = useSignOut();
  const go = (to) => { setOpen(false); history.push(to); };
  // About.dc.html: on an account page that is not Settings (About, Terms,
  // Privacy) the header shows that page as the current link left of Settings,
  // and Settings stays in its resting style.
  const page = isSettingsPath(pathname) ? null : ACCOUNT_MENU.find((m) => starts(pathname, m.to));
  const on = isSettingsPath(pathname);
  return (
    <>
      {page ? <Link to={page.to} className="ui-usermenu is-on" aria-current="page">{page.label}</Link> : null}
      <button
        ref={btn}
        type="button"
        className={cx('ui-usermenu', on && 'is-on')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-current={on ? 'page' : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        Settings
        <Chevron />
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchorEl={btn.current} placement="bottom-end" role="presentation">
        <Menu ariaLabel="Account">
          {ACCOUNT_MENU.map((m) => (
            <MenuItem key={m.key} on={m.key === 'settings' ? starts(pathname, '/profile/settings') || starts(pathname, '/profile/account') : starts(pathname, m.to)} onClick={() => go(m.to)}>
              {m.label}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem danger disabled={busy} onClick={signOut}>{busy ? 'Signing out…' : 'Sign out'}</MenuItem>
        </Menu>
      </Popover>
    </>
  );
}

function DesktopHeader({ pathname, risk, running }) {
  const section = activeSection(pathname);
  return (
    <header className="ui-header">
      {/* __in = the page frame, so the wordmark lines up with the content at every width */}
      <div className="ui-header__in">
        <Link to="/profile/portfolio" className="ui-wordmark">Cryptologics</Link>
        <nav className="ui-nav" aria-label="Main">
          {MAIN_NAV.map((n) => (
            <Link
              key={n.key}
              to={n.to}
              className={section === n.key ? 'is-on' : undefined}
              aria-current={section === n.key ? 'page' : undefined}
              data-tour={n.tour}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="ui-header__right">
          <RunningIndicator enabled={running} />
          <HaltChip risk={risk} />
          <UserMenu pathname={pathname} />
        </div>
      </div>
    </header>
  );
}

function PhoneHeader({ title, back, action, risk, running }) {
  const history = useHistory();
  const onBack = () => {
    if (typeof back === 'function') back();
    else if (typeof back === 'string') history.push(back);
    else history.goBack();
  };
  return (
    <header className="ui-phead">
      <div className="ui-phead__l">
        {back ? (
          <button type="button" className="ui-phead__back" aria-label="Back" onClick={onBack}>
            <BackArrow />
          </button>
        ) : null}
        <div className="ui-phead__title">{title}</div>
      </div>
      <div className="ui-phead__r">
        <RunningIndicator enabled={running} phone />
        {risk && risk.halted ? (
          <Link to="/profile/portfolio" className={cx('ui-haltchip', risk.global && 'ui-haltchip--global')} aria-label={risk.global ? 'Trading halted platform wide' : 'Trading halted'}>
            <Icon name="halt" size={14} />
            Halted
          </Link>
        ) : null}
        {action || null}
      </div>
    </header>
  );
}

function TabBar({ pathname, onMore, moreOpen }) {
  const tab = moreOpen ? 'more' : activeTab(pathname);
  return (
    <nav className="ui-tabbar" aria-label="Main">
      {TAB_BAR.map((t) => {
        const on = t.key === tab;
        const body = (
          <>
            <Icon name={t.icon} size={20} />
            {t.label}
          </>
        );
        if (t.key === 'more') {
          return (
            <button key={t.key} type="button" className={on ? 'is-on' : undefined} aria-haspopup="dialog" aria-expanded={moreOpen} onClick={onMore}>
              {body}
            </button>
          );
        }
        return (
          <Link key={t.key} to={t.to} className={on ? 'is-on' : undefined} aria-current={on ? 'page' : undefined} data-tour={t.tour}>
            {body}
          </Link>
        );
      })}
    </nav>
  );
}

export function MoreSheet({ open, onClose, pathname }) {
  const history = useHistory();
  const [signOut, busy] = useSignOut();
  const go = (to) => { onClose(); history.push(to); };
  return (
    <Layer open={open} onClose={onClose} label="More">
      <div className="ui-sheet">
        <div className="ui-sheet__grab" aria-hidden="true" />
        <div className="ui-sheet__title">More</div>
        {MORE_SHEET.map((m) => {
          const on = starts(pathname, m.to) || (m.key === 'settings' && isSettingsPath(pathname) && !starts(pathname, '/profile/subscription'));
          return (
            <button key={m.key} type="button" className={cx('ui-sheet__item', on && 'is-on')} aria-current={on ? 'page' : undefined} onClick={() => go(m.to)}>
              <Icon name={m.icon} size={20} />
              {m.label}
            </button>
          );
        })}
        <button type="button" className="ui-sheet__item ui-sheet__item--danger" onClick={signOut} disabled={busy}>
          <Icon name="signout" size={20} />
          {busy ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </Layer>
  );
}

/**
 * AppShell props:
 *   children  the routed page
 *   riskWatch false to skip the halt poll + risk:blocked socket (tests)
 *   runningWatch  the Running indicator's poll (GET /api/backtest/active);
 *             follows riskWatch unless given
 */
export default function AppShell({ children, riskWatch = true, runningWatch = riskWatch }) {
  const location = useLocation();
  const pathname = location.pathname;
  const isPhone = useIsPhone();
  const [moreOpen, setMoreOpen] = useState(false);
  const [phoneHeader, setPhoneHeaderState] = useState(null);
  const suppressToast = RISK_PANEL_ROUTES.some((r) => starts(pathname, r));
  const risk = useRiskWatch({ enabled: riskWatch, suppressToast });
  // Socket events → the shared cache (ui/data/liveCache), for as long as the
  // shell is up, so a page's cached bots / history / feed stay current while
  // that page is closed.
  useBotRoomSync(risk.socket || null, { shared: true });
  useBotFeedSync(risk.socket || null);

  const setPhoneHeader = useCallback((cfg) => setPhoneHeaderState(cfg || null), []);
  const resetPhoneHeader = useCallback(() => setPhoneHeaderState(null), []);

  const ctx = useMemo(
    () => ({ inShell: true, isPhone, setPhoneHeader, resetPhoneHeader, risk, socket: risk.socket || null }),
    [isPhone, setPhoneHeader, resetPhoneHeader, risk],
  );

  // Fixed-position widgets outside the shell (GettingStarted, legacy toast
  // containers) read this to sit above the 64 px phone tab bar.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--ui-tabbar-offset', isPhone ? '64px' : '0px');
    return () => root.style.removeProperty('--ui-tabbar-offset');
  }, [isPhone]);

  const title = (phoneHeader && phoneHeader.title) || defaultPhoneTitle(pathname);

  return (
    <ShellContext.Provider value={ctx}>
      <div className={cx('ui-root', 'ui-shell', isPhone && 'ui-shell--phone')}>
        {isPhone ? (
          <PhoneHeader title={title} back={phoneHeader && phoneHeader.back} action={phoneHeader && phoneHeader.action} risk={risk} running={runningWatch} />
        ) : (
          <DesktopHeader pathname={pathname} risk={risk} running={runningWatch} />
        )}
        <div className="ui-shell__main">{children}</div>
        {isPhone ? <TabBar pathname={pathname} moreOpen={moreOpen} onMore={() => setMoreOpen(true)} /> : null}
        {isPhone ? <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} pathname={pathname} /> : null}
        <ToastViewport />
      </div>
    </ShellContext.Provider>
  );
}
